import { IAllEventsMap } from "@/core/events/allEventsMap";
import { createLogger, ILogger } from "@/infrastructure/services/logger";
export class EventRejection extends Error {
  constructor(message: string) {
      super(message);
      this.name = 'EventRejection';
  }
}

type EventHandler<T> = (payload: T) => void | Promise<void>;

interface EmitOptions {
  timeout?: number;
  onTimeout?: (event: string, handlerIndex: number) => void;
  onReject?: (event: string, handlerIndex: number, reason: string) => void;
  onNoSubscribers?: (event: string) => void;
}

export class EventBus<TEvents extends Record<string, any>> {
  private readonly logger: ILogger = createLogger("EventBus");
private listeners: {
  [K in keyof TEvents]?: EventHandler<TEvents[K]>[];
} = {};

private static instance: EventBus<any>;

private constructor() {}

public static getInstance<T extends Record<string, any>>(): EventBus<T> {
  if (!EventBus.instance) {
    EventBus.instance = new EventBus();
  }
  return EventBus.instance;
}

public on<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
  if (!this.listeners[event]) this.listeners[event] = [];
  this.listeners[event]!.push(handler);
}

public off<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
  const handlers = this.listeners[event];
  if (!handlers) return;
  this.listeners[event] = handlers.filter((h) => h !== handler);
}

public async emit<K extends keyof TEvents>(
  event: K,
  payload: TEvents[K],
  options: EmitOptions = {}
): Promise<void> {
  const handlers = this.listeners[event];
  if (!handlers || handlers.length === 0) {
    const message = `⚠️ Event "${String(event)}" emitted but no subscribers found`;
    options.onNoSubscribers?.(String(event));
    this.logger.warn(message);
    return;
  }

  const timeout = options.timeout ?? 1000;

  await Promise.all(
    handlers.map(async (handler, index) => {
      const handlerPromise = (async () => {
        try {
          await handler(payload);
        } catch (error) {
          if (error instanceof EventRejection) {
            const message = `⚠️ Event "${String(event)}" listener[${index}] rejected: ${error.message}`;
            options.onReject?.(String(event), index, error.message);
            this.logger.warn(message);
          } else {
            this.logger.error(`❌ Error in handler for "${String(event)}":`, error);
          }
          throw error;
        }
      })();

      const timeoutPromise = new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), timeout)
      );

      try {
        await Promise.race([handlerPromise, timeoutPromise]);
      } catch (error) {
        if (error instanceof Error && error.message === 'timeout') {
          const message = `⚠️ Event "${String(event)}" listener[${index}] did not acknowledge within ${timeout}ms`;
          options.onTimeout?.(String(event), index);
          this.logger.warn(message);
        }
      }
    })
  );
}

public emitOnce<K extends keyof TEvents>(event: K, handler: EventHandler<TEvents[K]>): void {
  const onceHandler = (payload: TEvents[K]) => {
    handler(payload);
    this.off(event, onceHandler);
  };
  this.on(event, onceHandler);
}

public clearAll(): void {
  for (const key of Object.keys(this.listeners)) {
    delete this.listeners[key as keyof TEvents];
  }
}

public getSubscriberCount<K extends keyof TEvents>(event: K): number {
  return this.listeners[event]?.length || 0;
}
}


export const eventBus = EventBus.getInstance<IAllEventsMap>();
